import { IEntity } from '@kurtosys/types/api/search/IEntity.js';
import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { TConditionalHelperDependencies } from '@kurtosys/types/helpers/TConditionalHelperDependencies.js';
import { IExecutionOptions, IQueryContext, IQueryOptions } from '@kurtosys/types/query/index.js';
import { IQueryOptionsEntityBase } from '@kurtosys/types/query/options/IQueryOptionsEntityBase.js';

import { commonUtils, helpers } from '../../../common/index.js';
import { queryHelper } from '../../queryUtils/index.js';

import { BaseQuery } from './BaseQuery.js';

export const MAX_CALL_STACK_LEVEL = 50;

export abstract class BaseEntityQuery<TChildOptions extends IQueryOptionsEntityBase> extends BaseQuery<TChildOptions> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	getSubCollection(executionOptions: IExecutionOptions): any[] {
		const { queryOptionsType } = this.queryOptions;
		return this.processEntities(executionOptions, (entity) => {
			if (entity && entity[queryOptionsType]) {
				return entity[queryOptionsType];
			}
		});
	}
	processEntities(
		executionOptions: IExecutionOptions,
		processingFunction: (entity: IEntity) => any | any[] | undefined,
	): any | any[] | undefined {
		const conditionalHelperDependencies: TConditionalHelperDependencies = {
			queryClass: this.dependencies.queryClass,
		};
		const conditionalHelper = new helpers.ConditionalHelper(
			conditionalHelperDependencies,
			{ conditions: [] },
			this.translationHelper,
		);
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			const { entityByType, documentEntityByKeys } = context;
			const { entityType, entityConditional, documentEntityKey, entityKey, entityIndex } = this.options;

			let entities: IEntity[] = [];
			if (entityKey) {
				entities = this.getLinkedEntities(entityKey, context, entityIndex);
				if (entities && entityConditional) {
					entities = entities.filter((entity) =>
						conditionalHelper.matches(entity, entityConditional, executionOptions),
					);
				}
			} else {
				const documentEntity =
					documentEntityByKeys && documentEntityKey && documentEntityByKeys[documentEntityKey];
				const entity = documentEntity || (entityByType && entityType && entityByType[entityType]);
				if (
					entity &&
					(!entityConditional || conditionalHelper.matches(entity, entityConditional, executionOptions))
				) {
					entities = [entity as IEntity];
				}
			}
			const processingResults = [];
			for (const entity of entities) {
				processingResults.push(processingFunction(entity));
			}
			return processingResults.length === 0
				? undefined
				: processingResults.length === 1
				? processingResults[0]
				: processingResults;
		}
		return;
	}

	getEntities(executionOptions: IExecutionOptions, context: IQueryContext): IEntity[] | undefined {
		const conditionalHelperDependencies: TConditionalHelperDependencies = {
			queryClass: this.dependencies.queryClass,
		};
		const conditionalHelper = new helpers.ConditionalHelper(
			conditionalHelperDependencies,
			{ conditions: [] },
			this.translationHelper,
		);
		if (context) {
			const { entities } = context;
			const { queryId } = this.queryOptions;
			const { entityConditional } = this.options;
			const entitiesContext = entities && queryId && entities[queryId];
			if (entitiesContext) {
				return entitiesContext.values.filter(
					(entity) =>
						!entityConditional || conditionalHelper.matches(entity, entityConditional, executionOptions),
				) as IEntity[];
			}
		}
		return;
	}

	getLinkedEntities(entityKey: string, context: IQueryContext, entityIndex: number): IEntity[] {
		const { entityMap, entitiesById } = context;
		if (entityMap && entityKey && entityMap[entityKey]) {
			const linkedEntitiesIds = entityMap[entityKey];
			const linkedEntities = [];
			if (!commonUtils.isNullOrUndefined(entityIndex)) {
				const linkedEntityId = Array.from(linkedEntitiesIds)[entityIndex];
				if (linkedEntityId) {
					linkedEntities.push(entitiesById.get(linkedEntityId));
					return linkedEntities;
				}
				console.warn(
					`getLinkedEntities: No entity id found at childIndex of ${entityIndex} for entityKey "${entityKey}". Using all linked entities`,
				);
			}
			linkedEntitiesIds.forEach((linkedEntityId) => {
				linkedEntities.push(entitiesById.get(linkedEntityId));
			});
			return linkedEntities;
		}
		return [];
	}
}
